import React, { useEffect } from "react";
import { Form, Row, Col, Container } from "react-bootstrap";
import PropTypes from 'prop-types';
import { object, string } from 'yup';
import { useForm } from 'react-hook-form/dist/react-hook-form.ie11';
import FormControl from '@material-ui/core/FormControl';
import FormLabel from '@material-ui/core/FormLabel';
import BackNext from '../../Common/SubComponents/BackNext';
import { CheckboxInput, RadioInput, StyledRadioInput } from "../../../Helpers/Inputs";
import { ConvertStringToBoolean, ConvertBooleanToString } from "../../../Helpers/Formatters/ConvertBoolean";
import { CALIFORNIA, FLORIDA, NORTH_DAKOTA } from '../../../Helpers/Constants/States';
import Age from '../../../Helpers/Dates/Age';
import SvgIcon from '@material-ui/core/SvgIcon';
import Step5Icon from '../icons/step_5.svg';

export default function MedicalHistory(props) {

  const { register, errors, handleSubmit, setValue, triggerValidation } = useForm({
    validationSchema: MedicalHistory.ValidationSchema,
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });

  const noneOfTheAboveDisabled = (
    props.formState.alcoholOrDrugAbuse === "true" ||
    props.formState.heartDisease === "true" ||
    props.formState.stroke === "true" ||
    props.formState.lungDisease === "true" ||
    props.formState.depressionBipolar === "true" ||
    props.formState.cognitiveDisorder === "true" ||
    props.formState.muscularDystrophy === "true" ||
    props.formState.kidneyLiverDisease === "true" ||
    props.formState.leukemia === "true" ||
    props.formState.arthritis === "true" ||
    props.formState.sleepApnea === "true"
  );

  const state = props.basicInfo.state;
  const insuredAge = Age(props.basicInfo.birthdate);


  // uncheck "none" if it becomes disabled
  useEffect(() => {
    if (noneOfTheAboveDisabled && props.formState.none === "true") {
      props.onFieldChange('none', "false");
    } else if (noneOfTheAboveDisabled && props.formState.none !== "true") {
      triggerValidation('none');
    }
  }, [noneOfTheAboveDisabled]);

  useEffect(() => {
    register({ name: 'nicotineUse' });
    setValue('nicotineUse', props.formState.nicotineUse);

    register({ name: 'useRecreationalMarijuana' });
    setValue('useRecreationalMarijuana', props.formState.useRecreationalMarijuana);

    register({ name: 'illegalDrugUse' });
    setValue('illegalDrugUse', props.formState.illegalDrugUse);

    register({ name: 'illegalPrescriptionDrugUse' });
    setValue('illegalPrescriptionDrugUse', props.formState.illegalPrescriptionDrugUse);

    register({ name: 'diabetesPriorToAge40' });
    setValue('diabetesPriorToAge40', props.formState.diabetesPriorToAge40);

    register({ name: 'diabetes' });
    setValue('diabetes', props.formState.diabetes);

    register({ name: 'insuredAge' });
    setValue('insuredAge', insuredAge);

    register({ name: 'insulinDependent' });
    setValue('insulinDependent', props.formState.insulinDependent);

    register({ name: 'complicationsfromDiabetes' });
    setValue('complicationsfromDiabetes', props.formState.complicationsfromDiabetes);

    register({ name: 'aids' });
    setValue('aids', props.formState.aids);

    register({ name: 'incompleteFollowup' });
    setValue('incompleteFollowup', props.formState.incompleteFollowup);

  }, [register]);

  useEffect(() => {
    if (props.formState.useRecreationalMarijuana !== "true") {
      props.onFieldChange('marijuanaFrequency', "");
    }
  }, [props.formState.useRecreationalMarijuana])

  const handleValueChange = (field, value) => {
    if (field === 'nicotineUse') {
      var form = { ...props.fullFormState };
      form.steps.basicInfo.tobacco = value;
      form.steps.medicalHistory.nicotineUse = value;
      setValue(field, value);
      props.onMultiFieldChange(form);
      triggerValidation(field);
    }
    else {
      setValue(field, value);
      props.onFieldChange(field, value);
      triggerValidation(field);
    }
  };

  const handleCheckBoxValue = (field, value) => {
    props.onFieldChange(field, ConvertBooleanToString(value));
  };

  let nicotineUsageRadios = [
    { value: "Within the last year", label: "Within the last year" },
    { value: "Within the last 13-36 months", label: "Within the last 13-36 months" },
    { value: "Over 36 months", label: "Over 36 months ago" },
    { value: "Never", label: "Never" }
  ];

  const getLabelForAids = () => {
    if (state === CALIFORNIA) {
      return " Have you ever been diagnosed, treated or prescribed medication by a licensed member of the medical profession for AIDS or ARC or had a positive test for HIV antibodies in connection with an application for insurance?";
    } else if (state === FLORIDA) {
      return "To the best of your knowledge and belief, have you ever tested positive for the HIV infection or been diagnosed by a licensed member of the Medical profession as having ARC or AIDS caused by the HIV infection or other sickness or conditions derived from such infection?";
    } else if (state === NORTH_DAKOTA) {
      return "Have you ever been diagnosed as having, been treated by a licensed member of the medical profession for AIDS, AIDS-related Complex or tested positive for antibodies to HIV?";
    } else {
      return "Have you ever been diagnosed by a licensed member of the medical profession or tested positive for Human Immunodeficiency Virus (HIV) or Acquired Immune Deficiency Syndrome (AIDS)?";
    }
  }

  const getLabelForMedicalIssues = () => {
    if (state === CALIFORNIA) {
      return "Have you ever been diagnosed with, or received care or treatment for";
    } else if (state === FLORIDA) {
      return "To the best of your knowledge and belief, in the past 10 years have you been diagnosed, received care or treatment for, or consulted with or been advised to seek treatment by a licensed member of the medical profession for";
    } else {
      return "In the past 10 years have you been diagnosed, received care or treatment for, or consulted with or been advised to seek treatment by a licensed member of the medical profession for";
    }
  }

  const getLabelForDiabetes = () => {
    if (state === CALIFORNIA) {
      return "Have you ever been diagnosed with, or received care or treatment for Diabetes?";
    } else if (state === FLORIDA) {
      return "To the best of your knowledge and belief, in the past 10 years have you been diagnosed, received care or treatment for, or consulted with or been advised to seek treatment by a licensed member of the medical profession for Diabetes?";
    } else {
      return "In the past 10 years have you been diagnosed, received care or treatment for, or consulted with or been advised to seek treatment by a licensed member of the medical profession for Diabetes?";
    }
  }

  return (
    <Container>
      <Row className="formPage justify-content-center">
        <Col lg={6}>
          <Row className="justify-content-center">
            <SvgIcon component={Step5Icon} className="headerIcon" />
          </Row>
          <h4 className="headerTitle">Reviewing Your Medical History</h4>
          <h5 className="headerSubtitle">This information replaces taking a medical exam.</h5>

          <form className="quote-form" onSubmit={handleSubmit(props.onNext)}>
            <Form.Group>
              <RadioInput
                name="nicotineUse"
                id="nicotineUse"
                label="When was the last time you used any nicotine products, including electronic cigarettes or vaping devices?"
                value={props.formState.nicotineUse}
                radios={nicotineUsageRadios}
                onChange={e => handleValueChange('nicotineUse', e.target.value)}
                errors={errors.nicotineUse}
                inputRef={register}
              />
            </Form.Group>

            <Form.Group>
              <StyledRadioInput
                name="useRecreationalMarijuana"
                id="useRecreationalMarijuana"
                label="Do you use Marijuana recreationally?"
                value={props.formState.useRecreationalMarijuana}
                options={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
                onChange={e => handleValueChange('useRecreationalMarijuana', e)}
                errors={errors.useRecreationalMarijuana}
              />
            </Form.Group>

            {props.formState.useRecreationalMarijuana === "true" &&
              <Form.Group>
                <RadioInput
                  name="marijuanaFrequency"
                  id="marijuanaFrequency"
                  label="What is the average frequency of your use?"
                  value={props.formState.marijuanaFrequency}
                  radios={[{ value: "Once a week", label: "Once a week" }, { value: "2-3 times a week", label: "2-3 times a week" }, { value: "More than 3 times a week", label: "More than 3 times a week" }]}
                  onChange={e => handleValueChange('marijuanaFrequency', e.target.value)}
                  errors={errors.marijuanaFrequency}
                  inputRef={register}
                />
              </Form.Group>
            }

            <Form.Group>
              <StyledRadioInput
                name="illegalDrugUse"
                id="illegalDrugUse"
                label="In the past 5 years, have you used cocaine, opiates, methamphetamines or any illegal drug?"
                value={props.formState.illegalDrugUse}
                options={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
                onChange={e => handleValueChange('illegalDrugUse', e)}
                errors={errors.illegalDrugUse}
              />
            </Form.Group>

            <Form.Group>
              <StyledRadioInput
                name="illegalPrescriptionDrugUse"
                id="illegalPrescriptionDrugUse"
                label="In the past 5 years, have you used prescription drugs other than as prescribed by a licensed member of the medical profession?"
                value={props.formState.illegalPrescriptionDrugUse}
                options={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
                onChange={e => handleValueChange('illegalPrescriptionDrugUse', e)}
                errors={errors.illegalPrescriptionDrugUse}
              />
            </Form.Group>

            <FormControl component="fieldset">
              <FormLabel component="legend">{getLabelForMedicalIssues()}:</FormLabel>
              <Form.Group className="checkboxes">
                <CheckboxInput
                  name="alcoholOrDrugAbuse"
                  id="alcoholOrDrugAbuse"
                  checked={ConvertStringToBoolean(props.formState.alcoholOrDrugAbuse)}
                  onChange={e => handleCheckBoxValue('alcoholOrDrugAbuse', e.target.checked)}
                  errors={errors.alcoholOrDrugAbuse}
                  inputRef={register}
                  label="Alcohol or drug abuse?"
                />
                <CheckboxInput
                  name="heartDisease"
                  id="heartDisease"
                  checked={ConvertStringToBoolean(props.formState.heartDisease)}
                  onChange={e => handleCheckBoxValue('heartDisease', e.target.checked)}
                  errors={errors.heartDisease}
                  inputRef={register}
                  label="Heart disease, heart surgery, heart failure, irregular heart rate?"
                />
                <CheckboxInput
                  name="stroke"
                  id="stroke"
                  checked={ConvertStringToBoolean(props.formState.stroke)}
                  onChange={e => handleCheckBoxValue('stroke', e.target.checked)}
                  errors={errors.stroke}
                  inputRef={register}
                  label="Stroke, mini-stroke, aneurysm?"
                />
                <CheckboxInput
                  name="lungDisease"
                  id="lungDisease"
                  checked={ConvertStringToBoolean(props.formState.lungDisease)}
                  onChange={e => handleCheckBoxValue('lungDisease', e.target.checked)}
                  errors={errors.lungDisease}
                  inputRef={register}
                  label="Chronic lung disease, emphysema, chronic bronchitis, cystic fibrosis, asthma requiring a daily steroid?"
                />
                <CheckboxInput
                  name="depressionBipolar"
                  id="depressionBipolar"
                  checked={ConvertStringToBoolean(props.formState.depressionBipolar)}
                  onChange={e => handleCheckBoxValue('depressionBipolar', e.target.checked)}
                  errors={errors.depressionBipolar}
                  inputRef={register}
                  label="Major depression, bipolar, schizophrenia, eating disorder?"
                />
                <CheckboxInput
                  name="cognitiveDisorder"
                  id="cognitiveDisorder"
                  checked={ConvertStringToBoolean(props.formState.cognitiveDisorder)}
                  onChange={e => handleCheckBoxValue('cognitiveDisorder', e.target.checked)}
                  errors={errors.cognitiveDisorder}
                  inputRef={register}
                  label="Cognitive disorder, Alzheimer’s, dementia, Parkinson’s, amyotrophic lateral sclerosis (ALS), seizure disorder?"
                />
                <CheckboxInput
                  name="muscularDystrophy"
                  id="muscularDystrophy"
                  checked={ConvertStringToBoolean(props.formState.muscularDystrophy)}
                  onChange={e => handleCheckBoxValue('muscularDystrophy', e.target.checked)}
                  errors={errors.muscularDystrophy}
                  inputRef={register}
                  label="Muscular dystrophy, multiple sclerosis, cerebral palsy, paralysis?"
                />
                <CheckboxInput
                  name="kidneyLiverDisease"
                  id="kidneyLiverDisease"
                  checked={ConvertStringToBoolean(props.formState.kidneyLiverDisease)}
                  onChange={e => handleCheckBoxValue('kidneyLiverDisease', e.target.checked)}
                  errors={errors.kidneyLiverDisease}
                  inputRef={register}
                  label="Kidney disease, liver disease, blood disorder?"
                />
                <CheckboxInput
                  name="leukemia"
                  id="leukemia"
                  checked={ConvertStringToBoolean(props.formState.leukemia)}
                  onChange={e => handleCheckBoxValue('leukemia', e.target.checked)}
                  errors={errors.leukemia}
                  inputRef={register}
                  label="Leukemia, melanoma, brain tumor or cancer other than basal cell skin cancer (BCC)?"
                />
                <CheckboxInput
                  name="arthritis"
                  id="arthritis"
                  checked={ConvertStringToBoolean(props.formState.arthritis)}
                  onChange={e => handleCheckBoxValue('arthritis', e.target.checked)}
                  errors={errors.arthritis}
                  inputRef={register}
                  label="Rheumatoid arthritis, lupus, scleroderma?"
                />
                <CheckboxInput
                  name="sleepApnea"
                  id="sleepApnea"
                  checked={ConvertStringToBoolean(props.formState.sleepApnea)}
                  onChange={e => handleCheckBoxValue('sleepApnea', e.target.checked)}
                  errors={errors.sleepApnea}
                  inputRef={register}
                  label="Sleep apnea, which is not treated by CPAP or BiPAP therapy?"
                />
                <CheckboxInput
                  name="none"
                  id="none"
                  checked={ConvertStringToBoolean(props.formState.none)}
                  onChange={e => handleCheckBoxValue('none', e.target.checked)}
                  errors={errors.none}
                  inputRef={register}
                  label="None of the above"
                  disabled={noneOfTheAboveDisabled}
                />
              </Form.Group>
            </FormControl>

            <Form.Group>
              <StyledRadioInput
                name="diabetes"
                id="diabetes"
                label={getLabelForDiabetes()}
                value={props.formState.diabetes}
                options={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
                onChange={e => handleValueChange('diabetes', e)}
                errors={errors.diabetes}
              />
            </Form.Group>

            {props.formState.diabetes === "true" &&
              <React.Fragment>
                <Form.Group>
                  <StyledRadioInput
                    name="diabetesPriorToAge40"
                    id="diabetesPriorToAge40"
                    label="Was it diagnosed prior to age 40?"
                    value={props.formState.diabetesPriorToAge40}
                    options={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
                    onChange={e => handleValueChange('diabetesPriorToAge40', e)}
                    errors={errors.diabetesPriorToAge40}
                  />
                </Form.Group>

                <Form.Group>
                  <StyledRadioInput
                    name="insulinDependent"
                    id="insulinDependent"
                    label="Are you insulin dependent?"
                    value={props.formState.insulinDependent}
                    options={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
                    onChange={e => handleValueChange('insulinDependent', e)}
                    errors={errors.insulinDependent}
                  />
                </Form.Group>

                <Form.Group>
                  <StyledRadioInput
                    name="complicationsfromDiabetes"
                    id="complicationsfromDiabetes"
                    label="Have you had diabetic complications such as nerve pain, skin ulcers, vision, or kidney problems?"
                    value={props.formState.complicationsfromDiabetes}
                    options={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
                    onChange={e => handleValueChange('complicationsfromDiabetes', e)}
                    errors={errors.complicationsfromDiabetes}
                  />
                </Form.Group>
              </React.Fragment>
            }

            <Form.Group>
              <StyledRadioInput
                name="aids"
                id="aids"
                label={getLabelForAids()}
                value={props.formState.aids}
                options={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
                onChange={e => handleValueChange('aids', e)}
                errors={errors.aids}
              />
            </Form.Group>

            <Form.Group>
              <StyledRadioInput
                name="incompleteFollowup"
                id="incompleteFollowup"
                label="In the past 2 years, has a licensed member of the medical profession recommended additional follow-up treatment which has not been completed yet?"
                value={props.formState.incompleteFollowup}
                options={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
                onChange={e => handleValueChange('incompleteFollowup', e)}
                errors={errors.incompleteFollowup}
              />
            </Form.Group>

            <BackNext onBack={props.onBack} />

          </form>
        </Col>
      </Row>
    </Container>
  )
}

MedicalHistory.propTypes = {
  formState: PropTypes.object.isRequired,
  onNext: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  onFieldChange: PropTypes.func.isRequired,
}
const eligibleMessage = "Based on your answer, the Proposed Insured is not eligible for this product.";

MedicalHistory.ValidationSchema = object().shape({
  nicotineUse: string().required('This field is required.'),
  useRecreationalMarijuana: string().required('This field is required.'),
  marijuanaFrequency: string().when("useRecreationalMarijuana", {
    is: "true",
    then: string().required('This field is required.').oneOf(["Once a week", "2-3 times a week"], eligibleMessage),
    otherwise: string().notRequired()
  }),
  illegalDrugUse: string().required('This field is required.').matches(/^false/, eligibleMessage),
  illegalPrescriptionDrugUse: string().required('This field is required.').matches(/^false/, eligibleMessage),

  alcoholOrDrugAbuse: string().required('This field is required.').matches(/^false/, eligibleMessage),
  heartDisease: string().required('This field is required.').matches(/^false/, eligibleMessage),
  stroke: string().required('This field is required.').matches(/^false/, eligibleMessage),
  lungDisease: string().required('This field is required.').matches(/^false/, eligibleMessage),
  depressionBipolar: string().required('This field is required.').matches(/^false/, eligibleMessage),
  cognitiveDisorder: string().required('This field is required.').matches(/^false/, eligibleMessage),
  muscularDystrophy: string().required('This field is required.').matches(/^false/, eligibleMessage),
  kidneyLiverDisease: string().required('This field is required.').matches(/^false/, eligibleMessage),
  leukemia: string().required('This field is required.').matches(/^false/, eligibleMessage),
  arthritis: string().required('This field is required.').matches(/^false/, eligibleMessage),
  sleepApnea: string().required('This field is required.').matches(/^false/, eligibleMessage),
  none: string().required()
    .when(['alcoholOrDrugAbuse', 'heartDisease', 'stroke', 'lungDisease', 'depressionBipolar', 'cognitiveDisorder', 'muscularDystrophy', 'kidneyLiverDisease', 'leukemia', 'arthritis', 'sleepApnea'], {
      is: (alcoholOrDrugAbuse, heartDisease, stroke, lungDisease, depressionBipolar, cognitiveDisorder, muscularDystrophy, kidneyLiverDisease, leukemia, arthritis, sleepApnea) => checkBoxValidation(alcoholOrDrugAbuse, heartDisease, stroke, lungDisease, depressionBipolar, cognitiveDisorder, muscularDystrophy, kidneyLiverDisease, leukemia, arthritis, sleepApnea),
      then: string().matches(/^true/, 'Please check one checkbox.'),
      otherwise: string().required('This field is required.')
    }),

  diabetes: string().required('This field is required.'),
  diabetesPriorToAge40: string().when("diabetes", {
    is: "true",
    then: string().when("insuredAge", {
      is: (insuredAge) => insuredAge < 40,
      then: string().required('This field is required.').matches(/^false/, eligibleMessage).matches(/^true/, "Invalid answer. Insured is under 40 years of age."),
      otherwise: string().required('This field is required.').matches(/^false/, eligibleMessage),
    }),
    otherwise: string().notRequired()
  }),
  insulinDependent: string().when("diabetes", {
    is: "true",
    then: string().required('This field is required.').matches(/^false/, eligibleMessage),
    otherwise: string().notRequired()
  }),
  complicationsfromDiabetes: string().when("diabetes", {
    is: "true",
    then: string().required('This field is required.').matches(/^false/, eligibleMessage),
    otherwise: string().notRequired()
  }),
  aids: string().required('This field is required.').matches(/^false/, eligibleMessage),
  incompleteFollowup: string().required('This field is required.').matches(/^false/, eligibleMessage),
})
function checkBoxValidation(alcoholOrDrugAbuse, heartDisease, stroke, lungDisease, depressionBipolar, cognitiveDisorder, muscularDystrophy, kidneyLiverDisease, leukemia, arthritis, sleepApnea) {
  if (alcoholOrDrugAbuse === "false" && heartDisease === "false" && stroke === "false" && lungDisease === "false" && depressionBipolar === "false" && cognitiveDisorder === "false" && muscularDystrophy === "false" && kidneyLiverDisease === "false" && leukemia === "false" && arthritis === "false" && sleepApnea === "false")
    return true;
  else
    return false;
}

MedicalHistory.DefaultFormState = (prefill = false,mctData = null) => {
  return {
    nicotineUse: prefill ? "Never" : (mctData && mctData.tobacco) || '',
    useRecreationalMarijuana: prefill ? "false" : "",
    marijuanaFrequency: prefill ? "Once a week" : "",
    illegalDrugUse: prefill ? "false" : "",
    illegalPrescriptionDrugUse: prefill ? "false" : "",
    alcoholOrDrugAbuse: "false",
    heartDisease: "false",
    stroke: "false",
    lungDisease: "false",
    depressionBipolar: "false",
    cognitiveDisorder: "false",
    muscularDystrophy: "false",
    kidneyLiverDisease: "false",
    leukemia: "false",
    arthritis: "false",
    sleepApnea: "false",
    none: prefill ? "true" : "false",
    diabetes: prefill ? "false" : "",
    diabetesPriorToAge40: prefill ? "false" : "",
    insulinDependent: prefill ? "false" : "",
    complicationsfromDiabetes: prefill ? "false" : "",
    aids: prefill ? "false" : "",
    incompleteFollowup: prefill ? "false" : "",
  }
}



// WEBPACK FOOTER //
// ./src/components/Quoters/Americo/steps/MedicalHistory.js